// React Query hooks for the manage-businesses feature.
// Ported from ops-portal/src/app/services/business-entity-management.service.ts.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { appConfig } from '@/api/appConfig';
import type { M3ApiResponse } from '@/types/api';
import type {
  BusinessEntityDetail,
  BusinessEntityListItem,
  FreezeUnfreezeRequest,
} from '@/types/business-entity';

export const BE_LIST_KEY = ['business-entities'] as const;
export const beDetailKey = (id: string) => ['business-entities', id] as const;

/** Raw API shape before plan-string normalization. */
interface RawBusinessEntity extends Omit<BusinessEntityListItem, 'subscriptionPlan' | 'planFrequency' | 'companyName'> {
  companyName: string | null;
  subscriptionPlan: string | null;
}

/**
 * Mirrors the normalization in BusinessEntityManagementService.loadBusinessEntities():
 *  - splits `subscriptionPlan` on '_' → plan name + frequency
 *  - filters out items without a companyName
 */
function normalizeList(raw: RawBusinessEntity[]): BusinessEntityListItem[] {
  return raw
    .filter((be): be is RawBusinessEntity & { companyName: string } => be.companyName !== null)
    .map((be) => {
      const parts = (be.subscriptionPlan ?? 'starter').split('_');
      return {
        id: be.id,
        businessEntityId: be.businessEntityId,
        companyName: be.companyName,
        isFreezed: be.isFreezed,
        businessType: be.businessType,
        subscriptionPlan: parts[0] ?? 'starter',
        planFrequency: parts.length > 1 ? (parts[1] ?? 'free') : 'free',
      };
    });
}

export function useBusinessEntitiesQuery() {
  return useQuery({
    queryKey: BE_LIST_KEY,
    queryFn: async () => {
      const { data } = await apiClient.get<M3ApiResponse<RawBusinessEntity[]>>(
        `${appConfig.adminApi}/getBusinessEntities`,
      );
      return normalizeList(data.result ?? []);
    },
  });
}

export function useBusinessEntityQuery(id: string) {
  return useQuery({
    queryKey: beDetailKey(id),
    queryFn: async () => {
      const { data } = await apiClient.get<M3ApiResponse<BusinessEntityDetail>>(
        `${appConfig.adminApi}/getBusinessEntityById/${id}`,
      );
      return data.result;
    },
    enabled: !!id,
  });
}

export function useFreezeUnfreezeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: FreezeUnfreezeRequest) => {
      const { data } = await apiClient.post<M3ApiResponse>(
        `${appConfig.adminApi}/freezeUnfreeze`,
        input,
      );
      return data;
    },
    onSuccess: () => {
      // Invalidate both the list and any cached detail entries.
      void qc.invalidateQueries({ queryKey: ['business-entities'] });
    },
  });
}
